import type { TFunction } from "i18next"
import type { Palette } from "@mui/material"
import { ScatterMarker } from "@mui/x-charts"
import fp from "lodash/fp"

import type { RecordType, ScatterPlotView } from "types/dashboard.types"
import type { MuiScatterConfig } from "types/muiScatter.types"
import type { FieldQualityMap } from "types/quality.types"

import { formatXYPlotData } from "helpers/formatters/dataFormatters"
import { getXYPlotBounds } from "helpers/formatters/plots/xyChart"
import {
  axisToFormatterProps,
  buildSeriesByKey,
  buildSeriesColorMap,
  createQualityScatterMarker,
  getFormatter,
  getQualityColors,
  getSeriesLabel,
  splitFormatter,
} from "./helpers"

interface muiScatterConfigParams {
  view: ScatterPlotView
  data: RecordType[]
  palette: Palette
  t: TFunction
  locale?: string
  fromDate: number
  toDate: number
  fieldQualityMap?: FieldQualityMap
}

export default function muiScatterConfig({
  view,
  data,
  palette,
  t,
  locale,
  fromDate,
  toDate,
  fieldQualityMap,
}: muiScatterConfigParams): MuiScatterConfig {
  const formattedPlotData = formatXYPlotData(data)
  const [[xDomainMin, xDomainMax], [yDomainMin, yDomainMax]] = getXYPlotBounds(
    view,
    formattedPlotData,
    fromDate,
    toDate,
  )
  const viewProperties = view.properties
  const fieldProperties = viewProperties.fieldProperties
  const fallbackColors = viewProperties.colors

  const fill = viewProperties.fill

  const seriesByKey = buildSeriesByKey(formattedPlotData, fill)

  const seriesColorMap = buildSeriesColorMap(seriesByKey, fallbackColors, fieldProperties)

  const xFormatter = splitFormatter(
    getFormatter(axisToFormatterProps(viewProperties.axes.x, viewProperties, locale)),
  )
  const yFormatter = getFormatter(
    axisToFormatterProps(viewProperties.axes.y, viewProperties, locale),
  )

  const formattedData = fp.flow(
    fp.groupBy((item: RecordType) => item._field),
    fp.mapValues((recordsByField: RecordType[]) => {
      return fp.map(
        (item: RecordType) => ({
          id: item._time,
          x: item._time,
          y: item._value,
        }),
        recordsByField,
      )
    }),
  )(formattedPlotData)

  const series = fp.toPairs(seriesByKey).map(([key, value]) => ({
    id: key,
    type: "scatter" as const,
    label: getSeriesLabel(value, t),
    markerSize: viewProperties.markerSize,
    color: seriesColorMap[key],
    data: formattedData[value._field] as any[], // ScatterValueType is wrongly defined in @mui/x-charts
    highlightScope: { highlight: "item" as const, fade: "global" as const },
    valueFormatter: (value: any) => {
      // ScatterValueType is wrongly defined in @mui/x-charts
      return value && value.y !== null ? yFormatter(value.y) : null
    },
  }))

  const axisColor = palette.neutral[600]

  const xAxis = [
    {
      id: viewProperties.axes.x.key,
      scaleType: viewProperties.axes.x.scaleType,
      valueFormatter: xFormatter,
      tickLabelStyle: {
        fill: axisColor,
        fontWeight: 500,
        fontSize: 10,
      },
      domainLimit: () => ({
        min: xDomainMin,
        max: xDomainMax,
      }),
      height: 35,
      tickMinStep: viewProperties.axes.x.totalTicks
        ? (xDomainMax - xDomainMin) / viewProperties.axes.x.totalTicks
        : undefined,
    },
  ]

  const yAxis = [
    {
      valueFormatter: yFormatter,
      tickLabelStyle: {
        fill: axisColor,
        fontWeight: 500,
        fontSize: 10,
      },
      domainLimit: () => ({
        min: yDomainMin,
        max: yDomainMax,
      }),
      tickMinStep: viewProperties.axes.y.totalTicks
        ? (yDomainMax - yDomainMin) / viewProperties.axes.y.totalTicks
        : undefined,
      scaleType: viewProperties.axes.y.scaleType,
      width: 65,
    },
  ]

  const tooltip = viewProperties.tooltip
  const margin = viewProperties.margin
  const grid = viewProperties.grid
  const gridStyle = viewProperties.gridStyle ?? {
    stroke: palette.neutral[900],
    opacity: 0.05,
    strokeDasharray: "3, 3",
  }
  const xReferenceLines = viewProperties.xReferenceLines
  const yReferenceLines = viewProperties.yReferenceLines
  const legend = viewProperties.legend
  const qualityColors = getQualityColors(palette)
  const marker = fieldQualityMap
    ? createQualityScatterMarker(fieldQualityMap, formattedData, qualityColors)
    : ScatterMarker

  return {
    series,
    xAxis,
    yAxis,
    tooltip,
    margin,
    grid,
    gridStyle,
    xReferenceLines,
    yReferenceLines,
    legend,
    axisColor,
    xDomainMin,
    xDomainMax,
    marker,
  }
}
